/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  Image,
  Input,
  Text,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Control, FieldErrors, UseFormSetValue, useWatch, useFormState } from 'react-hook-form';
import { useState } from 'react';
import ImageIcon from '@/assets/svg/image-upload.svg?react';
import CustomButton from '@/components/common/Button';
import { useRemoveImage } from '@/services/addProduct';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import Asterisk from '../common/Asterisk';
import { type FormData } from '@/services/addProduct/types';
import useCustomToast from '../common/Toast';
import CustomConfirmationModal from '../common/ConfirmationModal';
import { IProductEditResponse } from '@/services/manageProduct/types';

interface ImageUploadSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  index: number;
  product?: IProductEditResponse;
  setValue: UseFormSetValue<FormData>;
  item: FormData['variants'][number];
}

const ImageUploadSection = ({ control, errors, index, setValue }: ImageUploadSectionProps) => {
  const { isSubmitted } = useFormState({ control });
  const [hoverIndex, setHoverIndex] = useState<{ variant: number | null; image: number | null }>({
    variant: null,
    image: null,
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<{ index: number; id: number } | null>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const { mutateAsync: removeImage, isLoading: isLoadingRemoveImage } = useRemoveImage();
  const { showToast } = useCustomToast();

  // Fixed: Use counters per slot instead of single state to prevent flickering
  const [dragCounters, setDragCounters] = useState<number[]>([0, 0, 0, 0]); // One counter for each of the 4 slots

  // Derived: Which slot (if any) is currently being dragged over
  const dragOverSlot = dragCounters.findIndex((count) => count > 0);
  // const isDraggingOverAny = dragOverSlot !== -1;

  // Watch the current variant's productImage array
  const currentImages =
    useWatch({
      control,
      name: `variants.${index}.productImage`,
    }) || [];

  const handleMultipleFileUpload = (files: File[]) => {
    const maxSize = 512 * 1024; // 512KB
    const maxFilenameLength = 100;

    let limitedFiles = files;
    if (files.length > 4) {
      showToast('error', 'Only 4 images are allowed. First 4 will be used.');
      limitedFiles = files.slice(0, 4);
    }

    const validFiles: File[] = [];
    limitedFiles.forEach((file) => {
      if (file.size > maxSize) {
        showToast('error', `File "${file.name}" exceeds 512KB limit.`);
        return;
      }

      // Truncate filename if too long
      let filename = file.name;
      if (filename.length > maxFilenameLength) {
        const ext = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '';
        const baseMax = maxFilenameLength - ext.length - 7; // room for _ + timestamp
        const base = filename.slice(0, filename.lastIndexOf('.') || filename.length);
        const truncated = base.slice(-baseMax); // take end of name to preserve meaningful part
        const timestamp = Date.now().toString().slice(-6);
        filename = `${truncated}_${timestamp}${ext}`;
      }
      const sanitizedFile = new File([file], filename, { type: file.type });
      validFiles.push(sanitizedFile);
    });

    if (validFiles.length === 0) return;

    // Rest of your logic (assign to empty slots)
    const currentProductImages =
      currentImages && Array.isArray(currentImages)
        ? [...currentImages]
        : Array(4)
            .fill(null)
            .map(() => ({ id: 0, url: '', file: undefined }));

    while (currentProductImages.length < 4) {
      currentProductImages.push({ id: 0, url: '', file: undefined });
    }

    const emptySlots: number[] = [];
    for (let j = 0; j < 4; j++) {
      const image = currentProductImages[j];
      const hasImage = image && ((image.url && image.url !== '') || image.file);
      if (!hasImage) emptySlots.push(j);
    }

    const numToAssign = Math.min(validFiles.length, emptySlots.length);
    for (let k = 0; k < numToAssign; k++) {
      const slot = emptySlots[k];
      const file = validFiles[k];
      const blobUrl = URL.createObjectURL(file);
      currentProductImages[slot] = {
        id: 0,
        url: blobUrl,
        file: file,
      };
    }

    setValue(`variants.${index}.productImage`, currentProductImages, {
      shouldValidate: isSubmitted,
    });
  };

  const handleImageDeleteConfirm = async () => {
    if (!imageToDelete) return;

    setIsConfirmLoading(true);

    try {
      if (imageToDelete.id === 0) {
        const currentProductImages = [...currentImages];
        currentProductImages[imageToDelete.index] = { id: 0, url: '', file: undefined };
        setValue(`variants.${index}.productImage`, currentProductImages, {
          shouldValidate: isSubmitted,
        });
      } else {
        await removeImage(imageToDelete.id);
        const currentProductImages = [...currentImages];
        currentProductImages[imageToDelete.index] = { id: 0, url: '', file: undefined };
        setValue(`variants.${index}.productImage`, currentProductImages, {
          shouldValidate: isSubmitted,
        });
      }
    } catch (error) {
      console.error('Error removing image:', error);
      showToast('error', 'Failed to remove image.');
      setIsConfirmLoading(false);
      return;
    }

    setIsConfirmLoading(false);
    setIsDeleteModalOpen(false);
    setImageToDelete(null);
  };

  const handleImageDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setImageToDelete(null);
  };

  const hasImageAtIndex = (imageIndex: number) => {
    const image = currentImages[imageIndex];
    return image && ((image.url && image.url !== '') || image.file);
  };

  const getImageSrc = (imageIndex: number) => {
    const image = currentImages[imageIndex];
    if (!image) return '';
    if (image.file instanceof File) {
      return URL.createObjectURL(image.file);
    }
    return image.url || '';
  };

  // Fixed drag handlers with counters
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, slotIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasImageAtIndex(slotIndex)) {
      setDragCounters((prev) => {
        const next = [...prev];
        next[slotIndex]++;
        return next;
      });
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>, slotIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounters((prev) => {
      const next = [...prev];
      next[slotIndex] = Math.max(0, next[slotIndex] - 1);
      return next;
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, slotIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset counter for this slot
    setDragCounters((prev) => {
      const next = [...prev];
      next[slotIndex] = 0;
      return next;
    });

    if (!hasImageAtIndex(slotIndex) && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith('image/'),
      );
      if (files.length > 0) {
        handleMultipleFileUpload(files);
      }
    }
  };

  return (
    <FormControl isInvalid={!!errors.variants?.[index]?.productImage} w="100%" overflowX="hidden">
      <FormLabel
        fontSize="sm"
        color={errors.variants?.[index]?.productImage ? 'status.error' : 'text.secondary'}
      >
        Product photo (Min 2 images needed)
        <Asterisk />
      </FormLabel>
      <Grid
        templateColumns={{
          base: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(auto-fit, minmax(200px, max-content))',
        }}
        gap={{ base: 2, sm: 4 }}
        alignItems="center"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Box
            key={i}
            display="flex"
            justifyContent={{ base: 'center', lg: 'start' }}
            alignItems="center"
            h={{ base: '150px', lg: '170px' }}
          >
            <Box
              {...(!hasImageAtIndex(i)
                ? {
                    as: 'label',
                    htmlFor: `upload-photo-${index}`,
                    cursor: 'pointer',
                  }
                : {
                    as: 'div',
                    cursor: 'default',
                  })}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              border="2px dashed"
              borderColor={
                dragOverSlot === i
                  ? 'brand.primary.DEFAULT'
                  : !hasImageAtIndex(i) && errors.variants?.[index]?.productImage
                    ? 'red'
                    : 'gray.400'
              }
              borderRadius="md"
              p={0}
              position="relative"
              transition="all 0.2s"
              _hover={{ bg: 'gray.100' }}
              boxSize={{ base: '150px', lg: '170px' }}
              onDragOver={handleDragOver}
              onDragEnter={(e: any) => handleDragEnter(e, i)}
              onDragLeave={(e: any) => handleDragLeave(e, i)}
              onDrop={(e: any) => handleDrop(e, i)}
            >
              {hasImageAtIndex(i) ? (
                <Box
                  position="relative"
                  boxSize="100%"
                  borderRadius="md"
                  overflow="hidden"
                  onMouseEnter={() => setHoverIndex({ variant: index, image: i })}
                  onMouseLeave={() => setHoverIndex({ variant: null, image: null })}
                >
                  <Image
                    src={getImageSrc(i)}
                    alt={`Thumbnail ${i + 1}`}
                    objectFit="cover"
                    boxSize={{ base: '150px', lg: '170px' }}
                    objectPosition="center 1px"
                  />
                  {hoverIndex.variant === index && hoverIndex.image === i && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      w="full"
                      h="full"
                      bg="rgba(0, 0, 0, 0.5)"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      transition="opacity 0.2s"
                    >
                      <CustomButton
                        isLoading={isLoadingRemoveImage}
                        variant="outline"
                        bg="white"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setImageToDelete({ index: i, id: currentImages[i]?.id || 0 });
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        Remove
                      </CustomButton>
                    </Box>
                  )}
                </Box>
              ) : (
                <Flex direction="column" gap={1} align="center" p={2}>
                  <Icon as={ImageIcon} w={8} h={8} color="gray.500" />
                  <Text
                    textAlign="center"
                    mt={2}
                    color="brand.primary.DEFAULT"
                    textDecor="underline"
                    cursor="pointer"
                  >
                    Click to upload
                  </Text>
                  {dragOverSlot === i && (
                    <Text fontSize="sm" color="brand.primary.DEFAULT" fontWeight="bold" mt={2}>
                      Drop here
                    </Text>
                  )}
                </Flex>
              )}
            </Box>
          </Box>
        ))}
      </Grid>
      <Input
        id={`upload-photo-${index}`}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          handleMultipleFileUpload(files);
          e.target.value = '';
        }}
      />
      <FormErrorMessage>{errors.variants?.[index]?.productImage?.message}</FormErrorMessage>

      <CustomConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleImageDeleteCancel}
        onConfirm={handleImageDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete Image?"
        description="Removing this image will permanently delete it from the system."
        buttonText="Delete"
        isLoading={isConfirmLoading}
      />
    </FormControl>
  );
};

export default ImageUploadSection;
